/* eslint-disable @typescript-eslint/no-explicit-any */
import * as CommonUtil from '../../common/js/CommonUtil';
import ConstData from '../js/ConstData';
import { ServerDomainArray, Result, FncImpl } from './SdkCommon';
import { TokenizedEventFunction } from './TokenizedEventsFnc';
import Logger from '../../common/js/Logger';

const PAGE_PATH_TOKENIZED = '/cko/page/tokenized/tokenized.html';
const FRAME_FOR_CYREXLIB = 'FRAME_FOR_CYREXLIB';

const cyEventFnc = new TokenizedEventFunction();
const cyResultGlobals = { resultCd: 'XXXX', resultMsg: '' };
let cyrexIframe: HTMLIFrameElement;
const cyrexVar = {
    serverDomain: '',
    publicKey: '',
    accToken: '',
    mode: '', 
}
type EmptyObject = Record<string, never>;
let bindParams: EmptyObject;

const cyrexObj = {
    getResult: () => cyResultGlobals,
    events: ConstData.events,
    /** 로컬환경에서는 반드시 호출하고 시작해야한다.
     * ex) fMode = 'local', fDomain = 'http://localhost:8080' */
    setForceMode: (forceMode: string, forceDomain: string) => {
        // eslint-disable-next-line no-console
        console.log(`set force mode and domain: ${forceMode}, ${forceDomain}`);        
        ServerDomainArray[forceMode] = forceDomain;
    },
    /* pk 키를 초기화 합니다. 페이지 호출시 반드시 호출 되어야 합니다. */
    init: (publicKeyParam: string, mode = 'prod') => {
        try {
            if(!CommonUtil.equals(mode, ['dev', 'local', 'sandbox', 'prod'])) {
                // eslint-disable-next-line no-console
                console.log(`invalid mode: ${mode}`);
                onError("INIT_EXCEPTION", `invalid mode: ${mode}`);
                return cyResultGlobals;
            }
            cyrexVar.mode = mode;
            cyrexVar.serverDomain = ServerDomainArray[cyrexVar.mode];
            
            /* 로거 초기화 */
            Logger.init(cyrexVar.mode);
            Logger.info('publicKeyParam:', publicKeyParam);
            if(!publicKeyParam) {
                return { resultCd: 'INIT_EXCEPTION', resultMsg: 'Undefined parameter' };
            } else {
                if(!publicKeyParam.startsWith('pk_')) {
                    Logger.warn('"publicKey" must start with "pk_" ', publicKeyParam);
                    onError("INIT_EXCEPTION", `"publicKey" must start with "pk_" : ${publicKeyParam}`);
                    return cyResultGlobals;
                } else {
                    cyrexVar.publicKey = publicKeyParam;
                }
            }
        } catch (error) {
            Logger.exception('You need to call an "init" function first.', error);
            onError("INIT_EXCEPTION", `You need to call an "init" function first.`);
            return cyResultGlobals;
        }
        return { resultCd: '0000', resultMsg: '' };
    },
    /* 결제 IFRAME 을 호출합니다. iframe을 감싸게 될 Element 정보를 반드시 포함해야 합니다. 
     * element는 bind 호출시점에 반드시 존재해야 합니다.
     * 여러번 재호출 할 수 있습니다.
    */
    bind: async (wrapper: string | EmptyObject, styleTemplate: 'default') => {
        Logger.info(`wrapper: ${wrapper} | StyleTemplate: ${styleTemplate}`);
        if (!cyrexVar.publicKey) {
            Logger.warn('You need to call an "init" function first.');
            onError("BIND_EXCEPTION", `You need to call an "init" function first.`);
            return cyResultGlobals;
        }
        let wrapperElementQuery = '';
        if(typeof wrapper == 'string') {
            wrapperElementQuery = wrapper;
        } else if(typeof wrapper == 'object') {
            bindParams = wrapper;
            if(!bindParams.wrapper) {
                onError("BIND_EXCEPTION", `invalid wrapper parameter`);
                return cyResultGlobals;
            }
        } else {
            onError("BIND_EXCEPTION", `invalid wrapper parameter`);
            return cyResultGlobals;
        }
        console.log('bindParams:', bindParams);
        try {
            const container = document.querySelector(wrapperElementQuery);
            if (!container) {
                Logger.warn(`wrapper not found: [${wrapper}]`);
                onError("BIND_EXCEPTION", `[${wrapper}] wrapper element not found`);
                return cyResultGlobals;
            }
            if(cyrexIframe) {
                cyrexIframe.remove();
            }
            cyrexIframe = document.createElement('iframe');
            container.appendChild(cyrexIframe);
            cyrexIframe.id = FRAME_FOR_CYREXLIB;
            cyrexIframe.style.width = '100%';
            cyrexIframe.style.height = '100%';
            cyrexIframe.style.borderWidth = '0';
            cyrexIframe.style.overflow = 'hidden';
            
            let key = cyrexVar.publicKey.replace('pk_', '').split('').reverse().join('');
            let encrypted = '';
            if(bindParams) {
                encrypted = window.btoa(CryptoJS.AES.encrypt(JSON.stringify(bindParams), key).toString());
            }
            cyrexIframe.src = `${cyrexVar.serverDomain}${PAGE_PATH_TOKENIZED}?publicKey=${cyrexVar.publicKey}&styleTemplate=${styleTemplate}&data=${encrypted}`;
            //cyrexIframe.src = `${cyrexVar.serverDomain}${PAGE_PATH_TOKENIZED}?publicKey=${cyrexVar.publicKey}&styleTemplate=${styleTemplate}`;
        } catch (error) {
            Logger.exception('Bind exception', error);
            onError("BIND_EXCEPTION", "Undefined exception. please retry again.");
        }
        return cyResultGlobals;
    },
    submit: async function () {
        try {
            if (cyrexIframe && cyrexIframe.contentWindow) {
                cyrexIframe.contentWindow.postMessage(ConstData.toServerEvents.SUBMIT + JSON.stringify({ 
                    publicKey: cyrexVar.publicKey, 
                    accToken: cyrexVar.accToken
                }), cyrexVar.serverDomain);
            }
        } catch (error) {
            Logger.exception('Submit exception', error);
            onError("SUBMIT_EXCEPTION", "Unable to submit. please check frame.");
        }
        return cyResultGlobals;
    },
    /** @author yhbae */
    addEventListener: function (event: string, func: FncImpl) {
        try {
            if (event == ConstData.events.READY) {
                cyEventFnc.READY = func;
            } else if (event == ConstData.events.VALIDATION_CHANGED) {
                cyEventFnc.VALIDATION_CHANGED = func;
            } else if (event == ConstData.events.SUBMITED) {
                cyEventFnc.SUBMITED = func;
            } else if (event == ConstData.events.TOKENIZED) {
                cyEventFnc.TOKENIZED = func;
            } else if (event == ConstData.events.ERROR) {
                cyEventFnc.ERROR = func;
            }
        } catch (error) {
            Logger.exception('addEventListener exception', error);
            onError("EVENT_EXCEPTION", "Undefined exception. please retry again.");
        }
        return cyResultGlobals;
    }
};


function setResult(result: any) {
    try {
        cyResultGlobals.resultCd = result.resultCd;
        cyResultGlobals.resultMsg = result.resultMsg;
    } catch (error) {
        cyResultGlobals.resultCd = 'UNDEFINED_EXCEPTION';
        cyResultGlobals.resultMsg = 'Undefined exception. please retry again';
        Logger.exception(`cyResultGlobals 세팅에 실패햇습니다 => ${result}`, error);
    }
}

function onReady(mchtObj: any) {
    setResult(mchtObj.result);
    if(mchtObj.result.resultCd == '0000') {
        cyrexVar.accToken = mchtObj.accToken;
        cyEventFnc.READY && cyEventFnc.READY(mchtObj);
    } else {
        setResult(mchtObj.result);
        cyEventFnc.ERROR && cyEventFnc.ERROR({result: cyResultGlobals});
    }
}

function onSubmit(mchtObj: any) {
    // eslint-disable-next-line no-console
    if(mchtObj.result.resultCd == '0000') {
        cyEventFnc.SUBMITED && cyEventFnc.SUBMITED(mchtObj);
    } else {
        setResult(mchtObj.result);
        cyEventFnc.ERROR && cyEventFnc.ERROR({result: cyResultGlobals});
    }
}
function onTokenized(mchtObj: any) {
    if(mchtObj.result.resultCd == '0000') {
        cyEventFnc.TOKENIZED && cyEventFnc.TOKENIZED(mchtObj);
    } else {
        setResult(mchtObj.result);
        cyEventFnc.ERROR && cyEventFnc.ERROR({result: cyResultGlobals});
    }
}
function onErrorMsg(mchtObj: any) {
    try {
        onError(mchtObj.result.resultCd, mchtObj.result.resultMsg);
    } catch (error) {
        Logger.exception('에러 메시지 수신오류(오브젝트가 올바르지 않음)', error);
    }
}

function onError(resultCd: string, resultMsg: string) {
    setResult({resultCd: resultCd, resultMsg: resultMsg});
    Logger.info('onError: %O', cyResultGlobals);
    cyEventFnc.ERROR && cyEventFnc.ERROR({result: cyResultGlobals});
}

window.addEventListener('message', function (e) {
    try {
        if (e.data && typeof e.data == 'string') {
            if (e.data.startsWith(ConstData.eventPrefix)) {
                if (e.data.startsWith(ConstData.events.READY)) {
                    Logger.info(Logger.tag('CLIENT REVC', 'yellow'), 'READY:', e.data);
                    onReady(JSON.parse(e.data.replace(ConstData.events.READY, '')));
                } else if (e.data.startsWith(ConstData.events.VALIDATION_CHANGED)) {
                    Logger.info(Logger.tag('CLIENT REVC', 'yellow'), 'VALIDATION_CHANGED:', e.data);
                    cyEventFnc.VALIDATION_CHANGED && cyEventFnc.VALIDATION_CHANGED(JSON.parse(e.data.replace(ConstData.events.VALIDATION_CHANGED, '')));
                } else if (e.data.startsWith(ConstData.events.TOKENIZED)) {
                    Logger.info(Logger.tag('CLIENT REVC', 'yellow'), 'TOKENIZED:', e.data);
                    onTokenized(JSON.parse(e.data.replace(ConstData.events.TOKENIZED, '')));
                } else if (e.data.startsWith(ConstData.events.SUBMITED)) {
                    Logger.info(Logger.tag('CLIENT REVC', 'yellow'), 'SUBMITED:', e.data);
                    onSubmit(JSON.parse(e.data.replace(ConstData.events.SUBMITED, '')));
                } else if (e.data.startsWith(ConstData.events.ERROR)) {
                    Logger.info(Logger.tag('CLIENT REVC', 'yellow'), 'ERROR:', e.data);
                    onErrorMsg(JSON.parse(e.data.replace(ConstData.events.ERROR, '')));
                } else {
                    Logger.info(Logger.tag('CLIENT REVC', 'yellow'), 'Undefined message:', e.data);
                }
            }
        }
    } catch (error) {
        Logger.exception('Recv postMessage exception', error);
        onError("UNDEFINED_EXCEPTION", "Undefined exception. please retry again.");
    }
});

export default cyrexObj;